import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { GroupsService } from '@shared/services/groups.service';
import { LoadingState } from '@shared/types/loading-state';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { Sorting } from '@shared/models/sorting';
import { regexList } from '@shared/constants/regex-list';
import { PresentationType } from '@shared/types/group-selector-type';
import { SessionService } from '@shared/services/session.service';
import { Session } from '@shared/models/session';
import { ModalController } from '@ionic/angular';
import { CustomersService } from '@features/customers/admin/services/customers.service';
import { GroupSelectorContainerComponent } from '@shared/components/group-selector-container/group-selector-container.component';
import { drawerEnter, drawerLeave } from '@animations/modal-drawer';
import { Permission } from '@shared/types/permission';
import { UserType } from '@shared/types/user-type';
import { Group } from '@shared/models/group';
import { GroupFilters } from '@shared/models/group-filters';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-group-selector',
  templateUrl: './group-selector.component.html',
  styleUrls: ['./group-selector.component.scss'],
  standalone: false,
})
export class GroupSelectorComponent implements OnInit, OnChanges {
  @Input() groupFilters: GroupFilters = {};
  @Input() presentation: PresentationType = 'row';
  @Input() showAddCustomer: boolean;
  @Input() showEsetParent: boolean = true;
  @Output() selectedGroup = new EventEmitter<Group>();

  groups$: Observable<Group[]>;
  sorting: Sorting = { key: 'id', direction: 'asc' };
  loadingState$: Observable<LoadingState>;
  paginationState$: Observable<PaginationState>;
  filters: GroupFilters = { ...this.groupFilters };
  regexList = regexList;
  groupSearchForm: FormGroup;
  isTyping: boolean = false;
  userType$: Observable<UserType | undefined>;
  session$: Observable<Session | undefined>;
  defaultParent: string = environment.root_group_id;
  companyName: string = environment.company_name;
  permissions$: Observable<Permission[] | undefined>;
  unusedFilters: { [key: string]: { label: string; value: string } } = {};
  defaultFilters: { [key: string]: string } = {
    id: 'Customer ID',
    name: 'Customer Name',
  };
  searchType: string;

  constructor(
    private renderer: Renderer2,
    private groupsService: GroupsService,
    private sessionService: SessionService,
    private customersService: CustomersService,
    private modalController: ModalController,
  ) {}

  ngOnInit(): void {
    this.filters = { ...this.groupFilters };
    this.userType$ = this.sessionService.getUserType();
    this.permissions$ = this.sessionService.getSessionPermissions();
    this.session$ = this.sessionService.getSession();
    this.paginationState$ = this.groupsService.getPaginationState();
    this.showAddCustomer = this.showAddCustomer || false;
    this.groupSearchForm = new FormGroup({
      groupSearchInput: new FormControl('', [Validators.required, Validators.minLength(3)]),
    });
    this.groupSearchForm.controls['groupSearchInput'].valueChanges
      .pipe(
        tap(() => {
          if (this.groupSearchForm.valid) {
            this.isTyping = true;
            setTimeout(() => {
              this.isTyping = false;
            }, 1000);
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
      )
      .subscribe((res) => {
        if (this.groupSearchForm.valid) {
          if (this.regexList.is_email.test(res!)) {
            this.filters = { ...this.groupFilters, primary_contact_email: res! };
          } else if (this.regexList.group_id.test(res!)) {
            this.filters = { ...this.groupFilters, id: res! };
          } else {
            this.filters = { ...this.groupFilters, name: res! };
          }
          this.groupsService.loadGroups(defaultPaginationState, this.sorting, this.filters);
        } else {
          this.groupsService.clearGroups();
        }
        const activeFilterKey = Object.keys(this.filters).find((key) => key in this.defaultFilters);
        this.searchType = activeFilterKey ? this.defaultFilters[activeFilterKey] : '';
        this.updateUnusedFilters();
      });
    this.groups$ = this.groupsService.getGroups();
    this.loadingState$ = this.groupsService.getGroupsLoading();
    this.groupsService.clearGroups();
  }

  updateUnusedFilters() {
    this.unusedFilters = {};

    Object.keys(this.defaultFilters).forEach((key) => {
      if (!(key in this.filters)) {
        this.unusedFilters[key] = {
          label: this.defaultFilters[key],
          value: key,
        };
      }
    });
  }

  searchByFilter(filterKey: string) {
    if (filterKey && this.groupSearchForm.valid) {
      const searchValue = this.groupSearchForm.controls['groupSearchInput'].value;
      this.filters = { ...this.groupFilters, [filterKey]: searchValue };
      this.searchType = this.defaultFilters[filterKey] || filterKey;
      this.groupsService.loadGroups(defaultPaginationState, this.sorting, this.filters);
      this.updateUnusedFilters();
    }
  }

  @ViewChild('searchInput') searchInput: ElementRef;

  ngOnChanges() {
    setTimeout(() => {
      if (this.searchInput.nativeElement && typeof this.searchInput.nativeElement.focus === 'function') {
        this.searchInput.nativeElement.focus();
      }
    }, 100);
  }

  selectedGroupEvent(group: Group) {
    this.groupsService.selectGroup(group);
    this.selectedGroup.emit(group);
  }

  selectFirstGroup() {
    this.groups$.pipe(take(1)).subscribe((groups) => {
      if (groups.length > 0) {
        this.selectedGroupEvent(groups[0]);
      }
    });
  }

  selectDefaultParent() {
    this.groupsService
      .getGroup(this.defaultParent)
      .pipe(
        take(1),
        switchMap((group) => {
          if (group) return of(group);

          this.groupsService.loadGroup(this.defaultParent);
          return this.groupsService.getGroup(this.defaultParent).pipe(
            filter((loadedGroup): loadedGroup is Group => !!loadedGroup),
            take(1),
          );
        }),
      )
      .subscribe((group) => this.selectedGroupEvent(group));
  }

  clearSearch() {
    this.groupSearchForm.controls['groupSearchInput'].setValue('');
    this.filters = { ...this.groupFilters };
    this.isTyping = false;
    this.groupsService.clearGroups();
    this.renderer.selectRootElement(this.searchInput.nativeElement).focus();
  }

  addCustomer() {
    this.session$.pipe(take(1), withLatestFrom(this.userType$)).subscribe(([session, userType]) => {
      if (userType == 'intranet' && session?.permissions?.includes('customers_upsert')) {
        this.presentParentGroupSelectorModal();
      } else {
        const group = session!.groups[0];
        this.customersService.selectAddCustomerParentGroup({ id: group.id, attributes: group } as Partial<Group>);
        this.modalController.dismiss({});
      }
    });
  }

  async presentParentGroupSelectorModal() {
    const modal = await this.modalController.create({
      component: GroupSelectorContainerComponent,
      componentProps: {
        title: 'Select Parent',
        groupFilters: { group_type: ['sub_distributor', 'partner', 'sub_distributor_partner'] },
      },
      cssClass: 'drawer-style-md',
      enterAnimation: drawerEnter,
      leaveAnimation: drawerLeave,
    });
    modal.onDidDismiss().then((result) => {
      if (result.data?.group) {
        const selectedGroup = result.data.group as Group;
        this.customersService.selectAddCustomerParentGroup(selectedGroup);
        this.modalController.dismiss({});
      }
    });
    return await modal.present();
  }
}
